import { api, throwApiError } from "../../../api/api";

// ── Tipos (contrato de /rgb no backend) ─────────────────────────────────────

export type RgbMovementType = "RECEBIMENTO" | "DEVOLUCAO_CDD" | "REFUGO" | "AJUSTE_SALDO";
export type RgbRefugoMotivo =
  | "QUEBRA"
  | "TRINCA"
  | "BOCA_LASCADA"
  | "DESGASTE"
  | "SUJIDADE"
  | "FORA_DE_PADRAO"
  | "OUTRO";

export type RgbConfig = {
  controleSaldo: boolean;
  fotoObrigatoria: boolean;
  metaRefugoMensalPorLoja: number | null;
  metaRefugoPercentual: number | null;
  /** Já filtrado pelo backend: saldo desligado → só Refugo; ajuste só para ADMIN. */
  tipos: { value: RgbMovementType; label: string }[];
  motivos: { value: RgbRefugoMotivo; label: string }[];
};

export type RgbStore = { id: string; nomeFantasia: string; estado: string | null };

export type RgbPackaging = {
  id: string;
  /** Código do item na planilha de contagem (ex.: D000213). */
  codigo: string | null;
  nome: string;
  descricao: string | null;
  valorUnitario: number | null;
  ativo: boolean;
  ordem: number;
};

export type RgbMovement = {
  id: string;
  store: { id: string; nomeFantasia: string };
  packaging: { id: string; nome: string };
  type: RgbMovementType;
  quantidade: number;
  /** Data civil, 'YYYY-MM-DD'. */
  data: string;
  motivo: RgbRefugoMotivo | null;
  observacao: string | null;
  fotoAssetIds: string[];
  source: "MANUAL" | "FILE";
  importBatchId: string | null;
  createdBy: { id: string; nomeCompleto: string };
  createdAt: string;
};

export type Paginated<T> = { page: number; limit: number; total: number; items: T[] };

export type RgbSummaryItem = {
  packaging: RgbPackaging;
  recebido: number;
  devolvido: number;
  refugado: number;
  ajustes: number;
  saldo: number | null;
  perdaEstimada: number | null;
};

export type RgbSummaryStore = {
  store: RgbStore;
  refugadoTotal: number;
  perdaEstimadaTotal: number | null;
  /** null = meta não definida ou período diferente de um mês calendário. */
  acimaDaMetaMensal: boolean | null;
  percentualRefugo: number | null;
  acimaDaMetaPercentual: boolean | null;
  itens: RgbSummaryItem[];
};

export type RgbSummary = { config: RgbConfig; lojas: RgbSummaryStore[] };

export type ImportError = { linha: number; coluna?: string; erro: string };

export type ImportPreviewRow = {
  linha: number;
  loja: string;
  vasilhame: string;
  type: RgbMovementType;
  quantidade: number;
  data: string;
  motivo: RgbRefugoMotivo | null;
  observacao: string | null;
};

export type ImportDryRun = {
  totalLinhas: number;
  validas: number;
  /** Itens com quantidade 0: sem refugo, ficam de fora. */
  ignoradas: number;
  erros: ImportError[];
  preview: ImportPreviewRow[];
};

export type ImportResult = { importBatchId: string; criados: number };

/** Loja e data escolhidas na tela: a planilha de contagem não traz as duas. */
export type ImportContext = { storeId?: string; data?: string };

export type CreateMovementPayload = {
  storeId: string;
  packagingId: string;
  type: RgbMovementType;
  quantidade: number;
  data: string;
  motivo?: RgbRefugoMotivo | null;
  observacao?: string | null;
  fotoAssetIds?: string[];
};

export type PackagingPayload = {
  nome?: string;
  codigo?: string | null;
  descricao?: string | null;
  valorUnitario?: number | null;
  ordem?: number;
  ativo?: boolean;
};

export type RgbFilters = {
  storeIds?: string[];
  packagingIds?: string[];
  type?: RgbMovementType;
  startDate?: string;
  endDate?: string;
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Filtros com array vão como `?storeIds=a&storeIds=b` — é o formato que o
 * backend normaliza. O axios, por padrão, mandaria `storeIds[]=a`.
 */
const toQuery = (params: Record<string, unknown>): URLSearchParams => {
  const q = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) value.forEach((v) => q.append(key, String(v)));
    else q.append(key, String(value));
  });
  return q;
};

const normalizeBearer = (token: string): string => {
  const t = String(token ?? "").trim();
  return t.toLowerCase().startsWith("bearer ") ? t.slice(7).trim() : t;
};

const apiBase = () => String(api.defaults.baseURL ?? "").replace(/\/+$/, "");

/** Baixa um blob como arquivo — mesmo mecanismo das telas de Estoque. */
export const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Resposta de erro de download vem como Blob JSON — lê a mensagem de dentro. */
const blobErrorMessage = async (error: any, fallback: string): Promise<string> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      return Array.isArray(json?.message) ? json.message.join(" | ") : json?.message ?? fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const download = async (path: string, params: Record<string, unknown>, filename: string, fallback: string) => {
  try {
    const { data } = await api.get(path, { params: toQuery(params), responseType: "blob" });
    saveBlob(data, filename);
  } catch (error) {
    throw new Error(await blobErrorMessage(error, fallback));
  }
};

// ── Endpoints ───────────────────────────────────────────────────────────────

export const getRgbConfigApi = async (): Promise<RgbConfig> => {
  try {
    return (await api.get<RgbConfig>("/rgb/config")).data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar a configuração do módulo.");
    throw error;
  }
};

export const getRgbStoresApi = async (): Promise<RgbStore[]> => {
  try {
    return (await api.get<RgbStore[]>("/rgb/stores")).data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar as lojas.");
    throw error;
  }
};

export const getRgbPackagingsApi = async (includeInactive = false): Promise<RgbPackaging[]> => {
  try {
    const { data } = await api.get<RgbPackaging[]>("/rgb/packagings", {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar os vasilhames.");
    throw error;
  }
};

export const createRgbPackagingApi = async (payload: PackagingPayload): Promise<RgbPackaging> => {
  try {
    return (await api.post<RgbPackaging>("/rgb/packagings", payload)).data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar o vasilhame.");
    throw error;
  }
};

export const updateRgbPackagingApi = async (id: string, payload: PackagingPayload): Promise<RgbPackaging> => {
  try {
    return (await api.patch<RgbPackaging>(`/rgb/packagings/${id}`, payload)).data;
  } catch (error) {
    throwApiError(error, "Erro ao salvar o vasilhame.");
    throw error;
  }
};

export const deleteRgbPackagingApi = async (id: string): Promise<void> => {
  try {
    await api.delete(`/rgb/packagings/${id}`);
  } catch (error) {
    throwApiError(error, "Erro ao excluir o vasilhame.");
  }
};

export const getRgbMovementsApi = async (
  filters: RgbFilters & { page?: number; limit?: number },
): Promise<Paginated<RgbMovement>> => {
  try {
    const { data } = await api.get<Paginated<RgbMovement>>("/rgb/movements", { params: toQuery(filters) });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar os apontamentos.");
    throw error;
  }
};

export const createRgbMovementApi = async (payload: CreateMovementPayload): Promise<RgbMovement> => {
  try {
    return (await api.post<RgbMovement>("/rgb/movements", payload)).data;
  } catch (error) {
    throwApiError(error, "Erro ao registrar o apontamento.");
    throw error;
  }
};

export const deleteRgbMovementApi = async (id: string): Promise<void> => {
  try {
    await api.delete(`/rgb/movements/${id}`);
  } catch (error) {
    throwApiError(error, "Erro ao excluir o apontamento.");
  }
};

export const getRgbSummaryApi = async (
  filters: RgbFilters & { startDate: string; endDate: string },
): Promise<RgbSummary> => {
  try {
    const { data } = await api.get<RgbSummary>("/rgb/summary", { params: toQuery(filters) });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o resumo.");
    throw error;
  }
};

export const downloadRgbSummaryExcelApi = (filters: RgbFilters & { startDate: string; endDate: string }) =>
  download(
    "/rgb/summary/excel",
    filters,
    `rgb_resumo_${filters.startDate}_${filters.endDate}.xlsx`,
    "Erro ao baixar o resumo em Excel.",
  );

export const downloadRgbMovementsExcelApi = (filters: RgbFilters) =>
  download("/rgb/movements/excel", filters, `rgb_movimentos_${Date.now()}.xlsx`, "Erro ao baixar os apontamentos.");

export const downloadRgbImportTemplateApi = () =>
  download("/rgb/import/template", {}, "modelo_contagem_vasilhame_rgb.xlsx", "Erro ao baixar o modelo.");

export class ImportRejectedError extends Error {
  readonly erros: ImportError[];

  constructor(message: string, erros: ImportError[]) {
    super(message);
    this.erros = erros;
    // Target es5: sem isto, `instanceof ImportRejectedError` dá false.
    Object.setPrototypeOf(this, ImportRejectedError.prototype);
  }
}

/**
 * Upload multipart vai por `fetch` (como a importação de usuários): o axios do
 * projeto fixa `Content-Type: application/json` e o navegador não escreveria o
 * `boundary`. Sem `confirm` o backend só valida; com `confirm` grava tudo ou nada.
 */
export async function importRgbMovementsApi(token: string, file: File, confirm: false, ctx?: ImportContext): Promise<ImportDryRun>;
export async function importRgbMovementsApi(token: string, file: File, confirm: true, ctx?: ImportContext): Promise<ImportResult>;
export async function importRgbMovementsApi(token: string, file: File, confirm: boolean, ctx: ImportContext = {}) {
  const form = new FormData();
  form.append("file", file);

  const query = toQuery({ confirm: confirm ? "true" : undefined, storeId: ctx.storeId, data: ctx.data }).toString();
  const res = await fetch(`${apiBase()}/rgb/import${query ? `?${query}` : ""}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${normalizeBearer(token)}`, Accept: "application/json" },
    body: form,
  });

  const text = await res.text();
  let json: any = null;
  try {
    json = JSON.parse(text);
  } catch {
    /* resposta não-JSON: fica o texto cru. */
  }

  if (!res.ok) {
    const message = Array.isArray(json?.message) ? json.message.join(" | ") : json?.message ?? (text || "Erro ao importar.");
    if (Array.isArray(json?.erros)) throw new ImportRejectedError(message, json.erros);
    throw new Error(message);
  }
  return json;
}

export const undoRgbImportApi = async (batchId: string): Promise<{ removidos: number }> => {
  try {
    return (await api.delete<{ removidos: number }>(`/rgb/import/${batchId}`)).data;
  } catch (error) {
    throwApiError(error, "Erro ao desfazer a importação.");
    throw error;
  }
};

/** Foto do refugo: GET /uploads/images/:assetId exige JWT, então vem como blob. */
export const getRgbPhotoBlobApi = async (token: string, assetId: string): Promise<Blob> => {
  const res = await fetch(`${apiBase()}/uploads/images/${encodeURIComponent(assetId)}`, {
    headers: { Authorization: `Bearer ${normalizeBearer(token)}` },
  });
  if (!res.ok) throw new Error("Não foi possível carregar a imagem.");
  return res.blob();
};
